import React, { useState, useRef, useEffect, lazy } from 'react';
import { createBrowserRouter, Outlet } from 'react-router';
import { RouterProvider } from 'react-router/dom';

import fetchy from './lib/fetchy';
import { Time } from './lib/Time';
import UserInfo from './components/UserInfo';
import { sum } from './lib/utils';

import Home from './pages/Home';
const SuperSuspense = lazy(() => import('./components/SuperSuspense'));
const Analytics = lazy(() => import('./components/Analytics'));
const Interface = lazy(() => import('./components/Interface'));
const SidebarProvider = lazy(() => import('./components/Sidebar/Context'));
const LoginSignup = lazy(() => import('./pages/LoginSignup'));

const router = createBrowserRouter([
	{
		path: "/",
		element: <Home />
	},
	{
		element: <><SuperSuspense><Outlet /></SuperSuspense><Analytics /></>,
		children: [
			{
				path: "/login",
				element: <LoginSignup mode="login" />
			},
			{
				path: "/signup",
				element: <LoginSignup mode="signup" />
			},
			{
				path: "/resetpassword1",
				element: <LoginSignup mode="resetpassword1" />
			},
			{
				path: "/resetpassword2",
				element: <LoginSignup mode="resetpassword2" />
			},
			{
				path: "/:page/:urlId?",
				element: <SidebarProvider><Interface /></SidebarProvider>
			}
		]
	}
]);

const App = () =>
{
	const [streak, setStreak] = useState();
	const [hearts, setHearts] = useState();
	const [planExpires, setPlanExpires] = useState();
	const [planName, setPlanName] = useState('init_visionary');
	const lastStreakMilestone = useRef();
	const email = useRef();
	const blockedUsers = useRef([]);

	const [unseen, setUnseen] = useState();
	const [guilds, setGuilds] = useState();
	const [applyAllGuilds, setApplyAllGuilds] = useState();
	const [welcomeChallenge, setWelcomeChallenge] = useState();
	const [quillmate, setQuillmate] = useState();
	const [levels, setLevels] = useState();
	const [cornerStats, setCornerStats] = useState();
	const [lock, setLock] = useState(false);
	const [notify, setNotify] = useState(false);
	const [timezone, setTimezone] = useState();

	const time = useRef(new Time(Date.now()));
	const userId = useRef();
	const [userIdState, setUserIdState] = useState();
	const userName = useRef();
	const [userNameState, setUserNameState] = useState();
	const anonymous = useRef(false);
	const systemMessage = useRef();
	const sentryLoaded = useRef(false);
	const [pwaInstaller, setPwaInstaller] = useState();

	useEffect(() => { (async() =>
	{
		await safeIdleCallback(async ()=>
		{
			const [response, {default:socky}] = await Promise.all([fetchy.get('/getuserinfo'), import('./lib/socky')]);
			if(response.data.anonymous)
			{
				setPlanName('');
				anonymous.current = true;
				socky.anonymous = true;
				return;
			} else
			{
				socky.emit('getuserinfo');
				localStorage.setItem('koalaQuillLoggedIn',1);
			}

			const {streak, hearts, planExpires, planName, now, unseen, guilds, apply_all_guilds, quillmate, vacation, deleted, levels, timezone, lock, notify, last_streak_milestone} = response.data;
			userId.current = response.data.userId;
			setUserIdState(response.data.userId);
			userName.current = response.data.name;
			setUserNameState(response.data.name);

			email.current = response.data.email;
			blockedUsers.current = response.data.blockedUsers;
			setStreak(typeof streak==='object' ? streak : {days:streak});
			lastStreakMilestone.current = last_streak_milestone;
			setHearts(hearts);
			setPlanName(planName);
			setLock(lock);
			setNotify(notify);
			if(planExpires) setPlanExpires(new Date(planExpires));
			time.current = new Time(now);

			systemMessage.current = response.data.system_message;
			setTimezone(timezone);

			setLevels(levels);
			setUnseen(unseen);
			setGuilds(guilds?.map(e => ({...e, noUpdate:true})));
			setApplyAllGuilds(apply_all_guilds);
			if(quillmate) setQuillmate({...quillmate, noUpdate:true});
			if(deleted) window.location.replace(`${process.env.REACT_APP_BACKEND_SERVER}/logout`);
			if(vacation)
			{
				const { notifyVacation } = await import('./lib/notify');
				notifyVacation();
			}
		}, { timeout:2000, timein:500, immediate:localStorage.getItem('koalaQuillLoggedIn') });

		if(!userId.current) return;

		const getWordsAndMinutes = data => ({ words:sum(data.map(e=>e.words)), minutes:sum(data.map(e=>e.minutes)) });
		const activityResponse = await fetchy.get('/getactivity');
		const activity = activityResponse?.data?.activity || [];

		const todayStart = new Date();			todayStart.setHours(0,0,0,0); 
		const today = activity.filter(e => new Date(e.start_time)>todayStart);

		setCornerStats({
			today: getWordsAndMinutes(today),
			total: getWordsAndMinutes(activity)
		});
	})(); }, []);

	useEffect(() =>
	{
		const install = e=>{ e.preventDefault(); setPwaInstaller(e); };
		window.addEventListener("beforeinstallprompt", install);
		return ()=>window.removeEventListener("beforeinstallprompt", install);
	}, []);

	// Preload Interface and notify
	useEffect(() => { safeIdleCallback(() => { import('./components/Interface'); import('./lib/notify');  }, { timeout:10000, timein:5000 }) }, []);

	/*
	 * See `Analytics.js` for actions that happen on `pathname` change:
	 * Scroll to top, ping mixpanel, setrefer, checklogin, set welcome challenge
	 */

	const context = { userId, userIdState, userName, userNameState, email, streak, setStreak, lastStreakMilestone, hearts, setHearts, cornerStats, setCornerStats, planName, planExpires, levels, setLevels, unseen, guilds, setGuilds, applyAllGuilds, setApplyAllGuilds, welcomeChallenge, setWelcomeChallenge, quillmate, setQuillmate, blockedUsers, lock, setLock, notify, setNotify, anonymous, systemMessage, timezone, sentryLoaded, pwaInstaller, time };

	return (<UserInfo context={context}><RouterProvider router={router} /></UserInfo>);
}

export default App;

/**
* Runs `callback` (sync or async) when the browser is idle. The returned Promise settles when the callback does.
* options.immediate - run now and skip idle scheduling
* options.timeout   - passed to requestIdleCallback: run after this many ms even if the browser never goes idle
* options.timein    - delay in ms for the setTimeout fallback, ONLY used when requestIdleCallback isn't available
*/
const DEFAULT_TIMEIN = 500;
const safeIdleCallback = (callback, options) => new Promise((resolve, reject) =>
{
	const run = async() =>
	{
		try
		{
			await callback();
			resolve();
		} catch(error)
		{
			reject(error);
		}
	}

	if(options?.immediate)
	{
		run();
	} else
	{
		try
		{
			requestIdleCallback(run, options);
		} catch
		{
			setTimeout(run, options?.timein||DEFAULT_TIMEIN);
		}
	}
});
